import axios from "axios";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

// ✅ Environment-based API URL configuration
// const API_BASE_URL =
//   window.location.hostname === "localhost"
//     ? "https://omantelsip.omantel.om"
//     : process.env.REACT_APP_SERVER_URL;
// const API_BASE_URL = "https://quiz-survey.onrender.com";
// const API_BASE_URL = "https://185.177.116.176";
const API_BASE_URL = "https://omandigitalservices.online";
// const API_BASE_URL = "https://164.68.98.76";
// const API_BASE_URL = "https://omantelsip.omantel.om";
// const API_BASE_URL = "http://localhost:8082";

// ✅ Allowed hosts for validation
const ALLOWED_HOSTS = [
  "omantelsip.omantel.om",
  "localhost:3000",
  "omandigitalservices.online",
];

// ✅ Validate current host
const validateHost = () => {
  const currentHost = window.location.host;
  if (!ALLOWED_HOSTS.includes(currentHost)) {
    console.error(`[Security Warning] Untrusted host detected: ${currentHost}`);
    // Log but don't break the app - let CORS handle security
    return false;
  }
  return true;
};

// Perform validation but don't throw
validateHost();

// ✅ Create Axios instance with proper configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookies
  timeout: 30000, // 30 second timeout
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// 🧩 Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add authorization token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Optional: Add CSRF token for additional security
    const csrfToken = localStorage.getItem("csrfToken");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }

    // Add custom request ID for debugging
    config.headers["X-Request-ID"] = `${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  },
);

// 🧩 Response Interceptor with proper error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network errors
    if (
      error.code === "ERR_NETWORK" ||
      error.message?.includes("Network Error")
    ) {
      console.error("Network error:", {
        url: error.config?.url,
        method: error.config?.method,
      });

      return Promise.reject({
        message: "Network error. Please check your internet connection.",
        originalError: error,
      });
    }

    // CORS errors
    if (error.message?.includes("CORS")) {
      console.error("CORS error - server configuration issue");
      return Promise.reject({
        message: "Unable to connect to server. Please contact support.",
        originalError: error,
      });
    }

    // HTTP errors
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.warn("Unauthorized - authentication required");
          localStorage.removeItem("token");

          // Dispatch custom event that components can listen to
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
          break;

        case 403:
          console.warn("Forbidden - insufficient permissions");
          break;

        case 404:
          console.warn("Resource not found:", error.config?.url);
          break;

        case 422:
          console.warn("Validation error:", data);
          break;

        case 429:
          console.warn("Rate limit exceeded");
          break;

        case 500:
        case 502:
        case 503:
          console.error("Server error - please try again later");
          break;

        default:
          console.warn("HTTP error:", status);
      }

      // Return structured error
      return Promise.reject({
        status,
        message: data?.message || error.message,
        data: data,
        originalError: error,
      });
    }

    // Timeout errors
    if (error.code === "ECONNABORTED") {
      return Promise.reject({
        message: "Request timeout. Please try again.",
        originalError: error,
      });
    }

    return Promise.reject(error);
  },
);

// 📡 WebSocket Management
let stompClient = null;
let webSocketConnected = false;

/**
 * Connect to WebSocket for real-time updates
 * @param {string} username - Username for targeting messages
 * @param {Function} onMandatoryPush - Callback when mandatory quiz is pushed
 * @returns {Function} Disconnect function
 */
const connectWebSocket = (username, onMandatoryPush) => {
  // Prevent multiple connections
  if (stompClient && webSocketConnected) {
    console.warn("⚠️ WebSocket already connected");
    return () => {};
  }

  const socket = new SockJS(`${API_BASE_URL}/api/user/ws`);

  stompClient = new Client({
    webSocketFactory: () => socket,
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,

    onConnect: () => {
      console.log("✅ WebSocket connected!");
      webSocketConnected = true;

      // Subscribe to quiz/survey channel
      stompClient.subscribe("/quizSurvey", (message) => {
        console.log("📩 Incoming WebSocket data:", message);

        try {
          const body = JSON.parse(message.body);
          console.log("🧾 Parsed message body:", body);

          // Check if message targets this user
          if (body.targetedUsers?.includes(username)) {
            console.log("🎯 Message targets user:", username);

            // Handle mandatory quiz push
            if (body.isMandatory && body.quizSurveyId) {
              if (onMandatoryPush) {
                onMandatoryPush({
                  quizSurveyId: body.quizSurveyId,
                  isMandatory: body.isMandatory,
                  data: body,
                });
              }
            } else {
              console.log("ℹ️ Push received, but not mandatory.");
            }
          } else {
            console.log("🚫 Message does not target this user");
          }
        } catch (e) {
          console.error("❌ Failed to parse WebSocket message:", e);
        }
      });
    },

    onStompError: (frame) => {
      console.error("❌ STOMP error:", frame.headers["message"]);
      webSocketConnected = false;
    },

    onWebSocketError: (event) => {
      console.error("❌ WebSocket error:", event);
      webSocketConnected = false;
    },

    onWebSocketClose: () => {
      console.log("🔌 WebSocket disconnected");
      webSocketConnected = false;
    },

    onDisconnect: () => {
      console.log("👋 WebSocket client disconnected");
      webSocketConnected = false;
    },
  });

  // Activate the connection
  stompClient.activate();

  // Return cleanup function
  return () => {
    if (stompClient) {
      console.log("🔌 Disconnecting WebSocket...");
      stompClient.deactivate();
      stompClient = null;
      webSocketConnected = false;
    }
  };
};

/**
 * Get WebSocket connection status
 * @returns {boolean} Connection status
 */
const isWebSocketConnected = () => {
  return webSocketConnected && stompClient?.connected;
};

// ✅ API methods with better error handling and documentation
const api = {
  // get all regions
  getAllRegions: async () => {
    return await apiClient.get("/api/user/regions");
  },
  /**
   * Fetch surveys based on user role
   * @param {string} userId - User ID
   * @param {boolean} isAdmin - Whether user is admin
   * @returns {Promise} Survey data
   */
  getSurveys: async (userId, isAdmin) => {
    const endpoint = isAdmin
      ? "/api/user/quiz-survey"
      : `/api/user/quiz-survey?userId=${userId}`;
    return await apiClient.get(endpoint);
  },

  /**
   * Update survey status
   * @param {string} id - Survey ID
   * @param {boolean} status - Active status
   */
  putSurveyStatus: async (id, status) => {
    return await apiClient.put(`/api/admin/quiz-survey/${id}`, { status });
  },

  /**
   * Update multiple survey statuses
   * @param {string[]} ids - Array of survey IDs
   * @param {boolean} status - Active status
   */
  patchMultipleSurveyStatus: async (ids, status) => {
    return await Promise.all(
      ids.map((id) =>
        apiClient.put(`/api/admin/quiz-survey/${id}`, { status }),
      ),
    );
  },

  /**
   * Delete a survey
   * @param {string} id - Survey ID
   */
  deleteSurvey: async (id) => {
    return await apiClient.delete(`/api/admin/quiz-survey/${id}`);
  },

  /**
   * Create announcement
   * @param {Object} payload - Announcement data
   */
  announceResult: async (payload) => {
    return await apiClient.post("/api/admin/announcements", payload);
  },

  /**
   * Publish a new quiz/survey
   * @param {Object} model - Quiz/survey data
   */
  publishQuiz: async (model) => {
    return await apiClient.post("/api/admin/quiz-survey", model);
  },

  /**
   * Fetch user announcements
   * @param {string} userId - User ID
   */
  fetchAnnouncements: async (userId) => {
    return await apiClient.get("/api/user/announcements", {
      params: { userId },
    });
  },

  /**
   * Mark all announcements as read
   * @param {string} userId - User ID
   */
  markAnnouncementsAsRead: async (userId) => {
    return await apiClient.put("/api/user/announcements/mark-all-read", {
      userId,
    });
  },

  /**
   * Fetch all users (admin only)
   */
  fetchUsers: async () => {
    return await apiClient.get("/api/user");
  },

  /**
   * Fetch mandatory surveys for user
   * @param {string} userId - User ID
   */
  fetchMandatorySurvey: async (userId) => {
    return await apiClient.get(`/api/user/quiz-survey?userId=${userId}`);
  },

  /**
   * Fetch quiz leaderboard
   * @param {string} quizId - Quiz ID
   */
  fetchLeaderboard: async (quizId) => {
    return await apiClient.get(`/api/user/quiz-survey/summary/${quizId}`);
  },

  /**
   * Fetch results for quiz or survey
   * @param {string} type - 'quiz' or 'survey'
   * @param {string} id - Quiz/Survey ID
   * @param {string} userRole - User role
   * @param {string} userId - User ID
   */
  fetchResults: async (type, id, userRole, userId) => {
    if (type === "quiz") {
      return userRole === "ADMIN"
        ? await apiClient.get(`/api/user/admin-quiz-result/${id}`)
        : await apiClient.get(`/api/user/quiz-result/${id}?userId=${userId}`);
    } else {
      return userRole === "ADMIN"
        ? await apiClient.get(`api/user/admin-survey-result/${id}`)
        : await apiClient.get(`/api/user/survey-result/${id}?userId=${userId}`);
    }
  },

  /**
   * Close a survey
   * @param {string} id - Survey ID
   */
  closeSurvey: async (id) => {
    return await apiClient.put(`/api/admin/quiz-survey/${id}`, {
      status: false,
    });
  },

  /**
   * Reopen survey with extended duration
   * @param {string} id - Survey ID
   * @param {number} extendMinutes - Minutes to extend
   */
  reopenSurvey: async (id, extendMinutes = 10) => {
    const currentSurvey = await apiClient.get(`/api/user/quiz-survey/${id}`);
    const currentDuration = parseInt(
      currentSurvey.data.data.quizTotalDuration,
      10,
    );
    const newDuration = currentDuration + extendMinutes;

    return await apiClient.put(`/api/admin/quiz-survey/${id}`, {
      status: true,
      quizTotalDuration: newDuration.toString(),
    });
  },

  /**
   * Fetch survey by ID
   * @param {string} id - Survey ID
   */
  fetchSurveyById: async (id) => {
    return await apiClient.get(`/api/user/quiz-survey/${id}`);
  },

  /**
   * Submit survey response
   * @param {string} id - Survey ID
   * @param {Object} payload - Response data
   */
  submitSurvey: async (id, payload) => {
    return await apiClient.post(`/api/user/submit/${id}`, payload);
  },

  /**
   * Fetch quiz insights (admin)
   * @param {string} quizId - Quiz ID
   */
  fetchQuizInsights: async (quizId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/quiz-insights/${quizId}`,
    );
  },

  /**
   * Fetch quiz completion statistics
   * @param {string} quizId - Quiz ID
   */
  fetchQuizCompletionStats: async (quizId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/completion-stats/${quizId}`,
    );
  },

  /**
   * Fetch survey response statistics
   * @param {string} surveyId - Survey ID
   */
  fetchSurveyResponseStats: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/survey-insights/${surveyId}`,
    );
  },

  /**
   * Fetch survey activity statistics
   * @param {string} surveyId - Survey ID
   */
  fetchSurveyActivityStats: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/survey-activity-stats/${surveyId}`,
    );
  },

  /**
   * Fetch survey satisfaction insights
   * @param {string} surveyId - Survey ID
   */
  fetchSurveySatisfactionInsights: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/satisfaction-insights/${surveyId}`,
    );
  },

  /**
   * Sync user data
   * @param {Object} userData - User data to sync
   */
  syncUser: async ({
    staffId,
    username,
    role,
    position,
    region,
    outlet,
    fcmToken,
  }) => {
    return await apiClient.post("/api/user/sync-user", {
      staffId,
      username,
      role,
      position,
      region,
      outlet,
      fcmToken,
    });
  },

  /**
   * Fetch segmentation data
   * @param {string} id - Survey ID
   */
  segmentation: async (id) => {
    return await apiClient.get(`/api/admin/quiz-survey/segmentation/${id}`);
  },

  /**
   * Get total staff invited count
   * @param {string} id - Survey ID
   */
  totalStaffInvited: async (id) => {
    return await apiClient.get(`/api/user/responses/staff-invited/${id}`);
  },

  /**
   * Get total responded users count
   * @param {string} id - Survey ID
   */
  totalRespondedUser: async (id) => {
    return await apiClient.get(`/api/user/responses/response-received/${id}`);
  },

  /**
   * Update survey configuration
   * @param {string} surveyId - Survey ID
   * @param {Array} userDataDisplayFields - Fields to display
   */
  updateSurveyConfig: async (surveyId, userDataDisplayFields) => {
    return await apiClient.put(`/api/admin/quiz-survey/${surveyId}`, {
      userDataDisplayFields,
    });
  },

  /**
   * Fetch least active users by region
   * @param {string} surveyId - Survey ID
   * @param {string} region - Region name
   */
  fetchLeastActiveUsersByRegion: (surveyId, region) => {
    return apiClient.get(
      `/api/user/survey-activity/least-active-users/region`,
      { params: { surveyId, region } },
    );
  },

  /* ===================== EXCEL EXPORT ===================== */

  /**
   * Download participation Excel for a single quiz/survey (ADMIN)
   * @param {string} quizSurveyId
   */
  downloadParticipationExcel: async (quizSurveyId) => {
    const response = await apiClient.get(
      `/api/admin/quiz-survey/participation/${quizSurveyId}/excel?_=${Date.now()}`,
      {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
        },
      },
    );

    // ✅ Extract filename from header
    const contentDisposition = response.headers["content-disposition"];
    let filename = "report.xlsx";

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename; // ✅ dynamic
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download overall quiz & survey participation Excel (ADMIN)
   */
  downloadOverallParticipationExcel: async () => {
    const response = await apiClient.get(
      `/api/admin/quiz-survey/participation/overall/excel?_=${Date.now()}`,
      { responseType: "blob" },
    );

    const contentDisposition = response.headers["content-disposition"];
    let filename = "Overall-Quiz-Survey-Report.xlsx";

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /* ===================== OFFERS ===================== */

  /**
   * Fetch all offers (ADMIN)
   */
  fetchAllOffers: async () => {
    return await apiClient.get("/api/user/offers");
  },

  /**
   * Fetch active offers (USER)
   */
  fetchActiveOffers: async () => {
    return await apiClient.get("/api/user/offers/active");
  },

  /**
   * Fetch offers for a specific user (USER)
   * @param {string} userId
   * @param {string} role
   */
  fetchOffersForUser: async (userId, role) => {
    return await apiClient.get(`/api/user/offers/user/${userId}`);
  },

  /**
   * Create offer (ADMIN)
   * @param {Object} payload
   */
  createOffer: async (payload) => {
    return await apiClient.post("/api/user/offers", payload);
  },

  /**
   * Update offer (ADMIN)
   * @param {string} offerId
   * @param {Object} payload
   */
  updateOffer: async (offerId, payload) => {
    return await apiClient.put(`/api/user/offers/${offerId}`, payload);
  },

  /**
   * Fetch ALL offers view report (ADMIN)
   */
  fetchAllOfferViewReport: async () => {
    return await apiClient.get("/api/admin/offers/view-report");
  },

  /**
   * Delete offer (ADMIN)
   * @param {string} offerId
   */
  deleteOffer: async (offerId) => {
    return await apiClient.delete(`/api/user/offers/${offerId}`);
  },

  /**
   * Fetch least active users by outlet
   * @param {string} surveyId - Survey ID
   * @param {string} outlet - Outlet name
   */
  fetchLeastActiveUsersByOutlet: (surveyId, outlet) => {
    return apiClient.get(
      `/api/user/survey-activity/least-active-users/outlet`,
      { params: { surveyId, outlet } },
    );
  },

  /* ===================== TRAINING & LEARNING ===================== */

  /**
   * Get Bunny TUS presigned upload signature (ADMIN)
   * @param {string} videoId
   */
  getTusSignature: async (videoId) => {
    return await apiClient.get("/api/user/training/bunny/tus-signature", {
      params: { videoId },
    });
  },

  createBunnyVideo: async (title) => {
    return await apiClient.post("/api/user/training/bunny/create-video", null, {
      params: { title },
    });
  },

  getBunnyUploadToken: async () => {
    return await apiClient.get("/api/user/training/bunny/upload-token");
  },

  /**
   * Get Bunny signed upload data (ADMIN)
   * Used for direct React → Bunny upload
   */
  getBunnyUploadSignature: async (fileSize, videoId) => {
    return await apiClient.get("/api/user/training/bunny/upload-signature", {
      params: { fileSize, videoId },
    });
  },

  uploadDocument: async (formData) => {
    return await apiClient.post(
      "/api/user/training/bunny/upload-document",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  },

  /**
   * Upload training material (ADMIN)
   * @param {Object} payload
   */
  uploadTraining: async (payload) => {
    return await apiClient.post("/api/user/training", payload);
  },

  /**
   * Fetch all training materials (ADMIN)
   */
  fetchAllTrainings: async () => {
    return await apiClient.get("/api/user/training");
  },

  /**
   * Fetch learner engagement by training ID (ADMIN)
   * @param {string} trainingId
   */
  fetchTrainingEngagementById: async (trainingId) => {
    return await apiClient.get(`/api/user/training/${trainingId}/engagement`);
  },

  /**
   * Fetch single training by ID (ADMIN / USER)
   * @param {string} trainingId
   */
  fetchTrainingById: async (trainingId) => {
    return await apiClient.get(`/api/user/training/${trainingId}`);
  },

  /**
   * Fetch training materials by region
   * @param {string} region
   */
  fetchTrainingsByRegion: async (region) => {
    return await apiClient.get(`/api/user/training/region/${region}`);
  },

  /**
   * Assign training to users (ADMIN)
   * @param {Object} payload
   * {
   *   trainingId,
   *   userIds: [],
   *   dueDate
   * }
   */
  assignTraining: async (payload) => {
    return await apiClient.post("/api/user/training/assign", payload);
  },

  /**
   * Fetch trainings assigned to a user (USER)
   * @param {string} userId
   */
  fetchUserTrainings: async (userId) => {
    return await apiClient.get(`/api/user/training/user/${userId}/details`);
  },

  /**
   * Update training progress (USER)
   * @param {Object} payload
   * {
   *   userId,
   *   trainingId,
   *   progress
   * }
   */
  updateTrainingProgress: async (payload) => {
    return await apiClient.post("/api/user/training/progress", payload);
  },

  updateTraining: async (trainingId, payload) => {
    return await apiClient.put(`/api/user/training/${trainingId}`, payload);
  },

  deleteTraining: async (trainingId) => {
    return await apiClient.delete(`/api/user/training/${trainingId}`);
  },

  fetchVideoEngagement: async () => {
    return await apiClient.get("/api/user/training/engagement");
  },
  downloadVideoEngagementExcel: async (trainingId) => {
    return apiClient.get("/api/user/training/engagement/excel", {
      params: trainingId ? { trainingId } : {},
      responseType: "blob",
      headers: {
        "Cache-Control": "no-cache",
      },
    });
  },

  /**
   * Fetch low scorers
   */
  fetchLowScorers: () => {
    return apiClient.get(`/api/user/responses/low-scorers`);
  },
  // get sinqleSurvey or quiz by staff ID
  // getSurveyScoreByStaffId: async (staffId) => {
  //   return await apiClient.get(
  //     `/api/admin/quiz-survey/user-score/by-staff/${staffId}`,
  //   );
  // },

  getSurveyScoreByStaffId: async (staffId, quarter, year) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/user-score/by-staff/${staffId}`,
      {
        params: {
          quarter, // Q1, Q2, Q3, Q4
          year,
        },
      },
    );
  },

  /**
   * Send notifications to users
   * @param {Object} payload - Notification data
   */
  notifyUsers: (payload) => {
    return apiClient.post(`/api/admin/announcements-with-targets`, payload);
  },

  /* ===================== NOTIFICATIONS ===================== */

  /**
   * Fetch all notifications for a user
   * @param {string} userId
   */
  fetchUserNotifications: async (userId) => {
    return await apiClient.get(`/api/user/notifications/${userId}`);
  },

  /**
   * Mark ALL notifications as read for a user
   * @param {string} userId
   */
  markAllNotificationsAsRead: async (userId) => {
    return await apiClient.put(`/api/user/notifications/read/${userId}`);
  },

  /**
   * Mark ONE notification as read
   * @param {string} notificationId
   */
  markNotificationAsRead: async (notificationId) => {
    return await apiClient.put(
      `/api/user/notifications/read-one/${notificationId}`,
    );
  },

  /**
   * Get unread notification count for badge
   * @param {string} userId
   */
  fetchUnreadNotificationCount: async (userId) => {
    return await apiClient.get(
      `/api/user/notifications/unread-count/${userId}`,
    );
  },

  /**
   * Check Bunny video processing status (ADMIN / USER)
   * @param {string} videoId - Bunny video GUID (NOT playback ID)
   * @returns {Promise<{status:number, encodeProgress:number}>}
   */
  checkBunnyVideoStatus: async (videoId) => {
    return await apiClient.get(
      `/api/user/training/bunny/video-status/${videoId}`,
    );
  },

  /* ===================== OFFER REPORT ===================== */

  uploadOfferImage: async (formData) => {
    return await apiClient.post(
      "/api/user/offers/bunny/upload-image",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
  },

  /**
   * Mark offer as viewed (USER)
   */
  markOfferAsViewed: async (offerId, userId) => {
    return await apiClient.put(`/api/user/offers/${offerId}/view`, null, {
      params: { userId },
    });
  },

  /**
   * Fetch offer view report (ADMIN)
   */
  fetchOfferViewReport: async (offerId) => {
    return await apiClient.get(`/api/admin/offers/${offerId}/view-report`);
  },

  /**
   * Download ALL offers view report Excel (ADMIN)
   */
  downloadAllOfferViewReportExcel: async () => {
    return await apiClient.get("/api/admin/offers/view-report/excel", {
      responseType: "blob",
      headers: {
        "Cache-Control": "no-cache",
      },
    });
  },

  // 📡 WebSocket Methods
  /**
   * Connect to WebSocket for real-time updates
   * @param {string} username - Username for targeting
   * @param {Function} onMandatoryPush - Callback for mandatory quiz push
   * @returns {Function} Disconnect function
   */
  connectWebSocket,

  /**
   * Check if WebSocket is connected
   * @returns {boolean} Connection status
   */
  isWebSocketConnected,
};

// Export both the api object and the client for advanced usage
export { apiClient };
export default api;
